import { PageCanvas } from "@/components/page-canvas";
import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FormErrors } from "@/components/forms/form-errors";
import {
  ServiceEditor,
  DiscountField,
  TotalsSummary,
  linePayload,
  changedServices,
  serviceCurrency,
  type EditorLine,
} from "@/components/commercial/service-editor";
import {
  locationRequestPath,
  locationRequestProposalDraftPath,
} from "@/routes";
import { Choice } from "../requests/controls";
type Offering = {
  id: string;
  revision: string;
  service_revision: string;
  name: string;
  price: string;
  currency: string;
  tax_rate: string;
  tax_label: string;
  tax_mode: string;
};
type InputValues = {
  assignee_id?: string;
  helper_ids?: string[];
  bay_id?: string;
  starts_at?: string;
  ends_at?: string;
  starts_at_offset?: string;
  ends_at_offset?: string;
  discount_amount?: string;
  services?: Partial<EditorLine>[];
};
export default function ProposalForm({
  enquiry,
  draft_revision,
  input,
  offerings,
  bays,
  time_choices,
  staff,
}: {
  enquiry: { id: string; revision: string; name: string };
  draft_revision: string | null;
  input: InputValues;
  offerings: Offering[];
  staff: { id: string; name: string }[];
  bays: { id: string; name: string }[];
  time_choices: Partial<Record<"starts_at" | "ends_at", string[]>>;
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  const options: EditorLine[] = offerings.map((offering) => ({
    id: offering.id,
    offering_id: offering.id,
    offering_revision: offering.revision,
    service_revision: offering.service_revision,
    name: offering.name,
    quantity: "1",
    unit_price: offering.price,
    discount_amount: "0",
    discount_percentage: "0",
    tax_mode: offering.tax_mode,
    tax_rate: offering.tax_rate,
    currency: offering.currency,
  }));
  const services = (input.services || []).flatMap((line) => {
    const option = options.find(
      (value) => value.offering_id === line.offering_id,
    );
    return option
      ? [
          {
            ...option,
            ...line,
            quantity: String(line.quantity ?? 1),
            unit_price: String(line.unit_price ?? option.unit_price),
            discount_amount: String(line.discount_amount ?? "0"),
            discount_percentage: String(line.discount_percentage ?? "0"),
          },
        ]
      : [
          {
            id: line.offering_id || "missing",
            name: t("workflow.unavailable_service"),
            quantity: String(line.quantity ?? 1),
            unit_price: String(line.unit_price ?? "0"),
            discount_amount: String(line.discount_amount ?? "0"),
            discount_percentage: String(line.discount_percentage ?? "0"),
            ...line,
          } as EditorLine,
        ];
  });
  const form = useForm(
    `Proposal:${enquiry.id}:${draft_revision || "new"}:${enquiry.revision}`,
    {
      request_revision: enquiry.revision,
      revision: draft_revision || "",
      proposal: {
        assignee_id: input.assignee_id || "",
        helper_ids: input.helper_ids || [],
        bay_id: input.bay_id || "",
        starts_at: input.starts_at || "",
        ends_at: input.ends_at || "",
        starts_at_offset: input.starts_at_offset || "",
        ends_at_offset: input.ends_at_offset || "",
        discount_amount: String(input.discount_amount || "0"),
        services,
      },
    },
  );
  if (!auth.location) return null;
  const location = auth.location.id,
    values = form.data.proposal;
  const set = (patch: Partial<typeof values>) =>
    form.setData("proposal", { ...values, ...patch });
  const stale =
    form.data.request_revision !== enquiry.revision ||
    form.data.revision !== (draft_revision || "");
  return (
    <AppLayout
      showWorkspaceNavigation={false}
      breadcrumbs={[
        {
          title: t("requests.title"),
          href: locationRequestPath(location, enquiry.id),
        },
      ]}
    >
      <Head title={t("proposals.compose")} />
      <PageCanvas>
        <div>
          <h1 className="text-page font-display">{t("proposals.compose")}</h1>
          <p className="mt-2">{enquiry.name}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("proposals.private_help")}
          </p>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.transform((data) => ({
              ...data,
              proposal: {
                ...data.proposal,
                services: data.proposal.services.map(linePayload),
              },
            }));
            form.post(locationRequestProposalDraftPath(location, enquiry.id));
          }}
        >
          <FormErrors
            errors={form.errors}
            targets={{
              bay: "proposal-bay",
              starts_at: "proposal-starts_at",
              ends_at: "proposal-ends_at",
              assignee: "proposal-assignee",
              quantity:
                values.services.length === 1
                  ? "proposal-quantity-0"
                  : "proposal-services",
              services: "proposal-services",
            }}
          />
          {stale && (
            <section className="space-y-3 rounded-lg border p-4">
              <p role="alert">{t("workflow.stale")}</p>
              <p>{enquiry.name}</p>
              <details>
                <summary className="cursor-pointer py-2">
                  {t("workflow.current_terms")}
                </summary>
                <p>
                  {input.starts_at} – {input.ends_at}
                </p>
                {input.services?.map((line, index) => (
                  <p key={index}>
                    {options.find(
                      (option) => option.offering_id === line.offering_id,
                    )?.name || t("jobs.services")}{" "}
                    · {line.quantity || 1} × {line.unit_price}
                  </p>
                ))}
              </details>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.setData({
                    ...form.data,
                    request_revision: enquiry.revision,
                    revision: draft_revision || "",
                  });
                  form.clearErrors();
                }}
              >
                {t("workflow.review_context")}
              </Button>
            </section>
          )}
          <div className="mt-5 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0 space-y-6">
              <ServiceEditor
                errors={form.errors}
                idPrefix="proposal"
                lines={values.services}
                options={options}
                onChange={(services) => set({ services })}
              />
              <fieldset className="space-y-4 rounded-lg border p-4">
                <legend className="px-1 font-semibold">
                  {t("jobs.interval")}
                </legend>
                <p className="text-sm text-muted-foreground">
                  {t("jobs.zone_help", { zone: i18n.time_zone })}
                </p>
                <Choice
                  id="proposal-bay"
                  name="proposal[bay_id]"
                  label={t("jobs.bay_id")}
                  options={bays}
                  defaultValue={values.bay_id}
                  onValueChange={(value) => set({ bay_id: value || "" })}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  {(["starts_at", "ends_at"] as const).map((field) => (
                    <div key={field} className="space-y-2">
                      <Label htmlFor={`proposal-${field}`}>
                        {t(`jobs.${field}`)}
                      </Label>
                      <Input
                        id={`proposal-${field}`}
                        type="datetime-local"
                        required
                        value={values[field]}
                        onChange={(event) =>
                          set({ [field]: event.target.value })
                        }
                        aria-invalid={
                          !!(form.errors as Record<string, unknown>)[field]
                        }
                      />
                      {(time_choices[field]?.length || 0) > 1 && (
                        <Choice
                          id={`proposal-${field}-offset`}
                          name={`proposal[${field}_offset]`}
                          label={t("jobs.occurrence")}
                          defaultValue={values[`${field}_offset`]}
                          onValueChange={(value) =>
                            set({ [`${field}_offset`]: value || "" })
                          }
                          options={time_choices[field]!.map(
                            (offset, index) => ({
                              id: offset,
                              name: t(
                                index
                                  ? "jobs.second_occurrence"
                                  : "jobs.first_occurrence",
                                { offset },
                              ),
                            }),
                          )}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </fieldset>
              <fieldset className="space-y-4 rounded-lg border p-4">
                <legend className="px-1 font-semibold">{t("jobs.team")}</legend>
                <Choice
                  id="proposal-assignee"
                  name="proposal[assignee_id]"
                  label={t("jobs.assignee_id")}
                  options={staff}
                  defaultValue={values.assignee_id}
                  onValueChange={(value) => set({ assignee_id: value || "" })}
                />
                <details>
                  <summary className="cursor-pointer py-2">
                    {t("jobs.helpers")}
                  </summary>
                  <div className="mt-3 space-y-3">
                    {staff.map((person) => (
                      <Label
                        key={person.id}
                        className="flex items-center gap-3"
                      >
                        <Checkbox
                          checked={values.helper_ids.includes(person.id)}
                          onCheckedChange={(checked) =>
                            set({
                              helper_ids: checked
                                ? [...values.helper_ids, person.id]
                                : values.helper_ids.filter(
                                    (id) => id !== person.id,
                                  ),
                            })
                          }
                        />
                        {person.name}
                      </Label>
                    ))}
                  </div>
                </details>
                <p className="text-sm text-muted-foreground">
                  {t("jobs.team_help")}
                </p>
              </fieldset>
            </div>
            <TotalsSummary
              lines={values.services}
              amount={values.discount_amount}
              percentage="0"
            >
              <DiscountField
                percentageEnabled={false}
                id="proposal-discount"
                amount={values.discount_amount}
                percentage="0"
                onChange={(amount) => set({ discount_amount: amount })}
              />
              <div className="flex flex-wrap gap-3">
                <Button
                  type="submit"
                  disabled={
                    form.processing ||
                    stale ||
                    !serviceCurrency(values.services) ||
                    changedServices(values.services, options)
                  }
                >
                  {t("proposals.save_preview")}
                </Button>
                <Button
                  variant="outline"
                  render={
                    <Link href={locationRequestPath(location, enquiry.id)} />
                  }
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </TotalsSummary>
          </div>
        </form>
      </PageCanvas>
    </AppLayout>
  );
}
